import { makeAutoObservable, runInAction, reaction, computed } from 'mobx'
import {
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore'
import FirebaseApp from '../../../shared/api/firebase'
import {
  eachDayOfInterval,
  getDay,
  format,
  parse,
  addHours,
  subHours,
} from 'date-fns'
import { getDocument } from '../../../shared/api/firestore'

const db = getFirestore(FirebaseApp)

class BookingStore {
  visits = []
  isLoading = false
  _fetchedRanges = [] // Для кэширования загруженных диапазонов дат
  selectedStaffId = null

  constructor(rootStore) {
    makeAutoObservable(
      this,
      {
        rootStore: false,
        calendarEvents: computed,
        timeGridBackgroundEvents: computed,
        viewableWorkTimeRange: computed,
        monthViewBackgroundEvents: computed,
        selectedStaffMember: computed,
      },
      { autoBind: true },
    )
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore
    this.clientsStore = rootStore.clientsStore // Needed to get client names for calendar events
    this.staffStore = rootStore.staffStore // Correct dependency

    reaction(
      () => this.organizationStore.organization?.id,
      (orgId) => {
        // При смене организации, очищаем все данные.
        // Календарь сам вызовет загрузку для нового видимого диапазона.
        this.clearVisits()
      },
      { fireImmediately: true },
    )
  }

  clearVisits() {
    this.visits = []
    this._fetchedRanges = []
  }

  setSelectedStaffId(staffId) {
    this.selectedStaffId = staffId
  }

  async fetchVisitsForDateRange(startDate, endDate) {
    const orgId = this.organizationStore.organization?.id
    if (!orgId) return

    // Проверяем, был ли этот диапазон уже загружен, чтобы избежать повторных запросов
    const isAlreadyFetched = this._fetchedRanges.some(
      (range) => startDate >= range.start && endDate <= range.end,
    )
    if (isAlreadyFetched) {
      return // Диапазон уже загружен
    }

    this.isLoading = true
    try {
      const bookingsColRef = collection(db, 'organizations', orgId, 'bookings')
      const q = query(
        bookingsColRef,
        where('startTime', '>=', startDate),
        where('startTime', '<=', endDate),
      )

      const querySnapshot = await getDocs(q)
      const newVisits = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      runInAction(() => {
        // Объединяем новые записи с существующими, избегая дубликатов
        const existingIds = new Set(this.visits.map((v) => v.id))
        const uniqueNewVisits = newVisits.filter((v) => !existingIds.has(v.id))
        this.visits.push(...uniqueNewVisits)

        // Кэшируем новый загруженный диапазон
        this._fetchedRanges.push({ start: startDate, end: endDate })
      })
    } catch (error) {
      console.error('Не удалось загрузить записи:', error)
      this.errorStore.showError('Не удалось загрузить список записей')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async addVisit(visitData) {
    const orgId = this.organizationStore.organization.id
    if (!orgId) {
      this.errorStore.showError('Организация не найдена.')
      return false
    }

    this.isLoading = true
    try {
      const visitCollectionRef = collection(
        db,
        'organizations',
        orgId,
        'bookings',
      )
      const newDocRef = doc(visitCollectionRef)

      const newVisitData = {
        ...visitData,
        status: 'confirmed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      await setDoc(newDocRef, newVisitData)

      runInAction(() => {
        // Оптимистичное обновление: создаем чистый объект для локального состояния,
        // чтобы избежать проблем с несериализуемыми плейсхолдерами serverTimestamp.
        const optimisticVisit = {
          ...visitData,
          id: newDocRef.id,
          status: 'confirmed',
          createdAt: new Date(),
          updatedAt: new Date(),
          startTime: visitData.startTime.toDate(),
          endTime: visitData.endTime.toDate(),
        }
        this.visits.push(optimisticVisit)
      })
      return true
    } catch (error) {
      console.error('Ошибка при создании записи:', error)
      this.errorStore.showError('Не удалось создать запись.')
      return false
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async updateVisit(visitId, data) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !visitId) return

    try {
      const visitDocRef = doc(db, 'organizations', orgId, 'bookings', visitId)
      await updateDoc(visitDocRef, {
        ...data,
        updatedAt: serverTimestamp(),
      })

      runInAction(() => {
        const index = this.visits.findIndex((v) => v.id === visitId)
        if (index !== -1) {
          this.visits[index] = { ...this.visits[index], ...data }
        }
        this.errorStore.showSuccess('Запись успешно обновлена.')
      })
    } catch (error) {
      console.error('Ошибка при обновлении записи:', error)
      this.errorStore.showError('Не удалось обновить запись.')
    }
  }

  async getVisitById(visitId) {
    console.log(
      `[BookingStore] getVisitById called for ID: ${visitId} with orgId: ${this.organizationStore.organization?.id}`,
    )
    const orgId = this.organizationStore.organization?.id
    if (!orgId || !visitId) {
      console.log(
        '[BookingStore] Aborting getVisitById: missing orgId or visitId.',
      )
      return null
    }

    // 1. Try to find in cache first for speed
    const cachedVisit = this.visits.find((v) => v.id === visitId)
    if (cachedVisit) {
      console.log(`[BookingStore] Found visit ${visitId} in cache.`)
      return cachedVisit
    }

    console.log(
      `[BookingStore] Visit ${visitId} not in cache. Fetching from Firestore...`,
    )
    // 2. If not in cache, fetch from Firestore
    try {
      const visitDoc = await getDocument([
        'organizations',
        orgId,
        'bookings',
        visitId,
      ])
      console.log(`[BookingStore] Firestore returned for ${visitId}:`, visitDoc)
      if (visitDoc) {
        // Add to cache for future use
        runInAction(() => {
          // Avoid duplicates if another fetch happened in the meantime
          if (!this.visits.some((v) => v.id === visitId)) {
            console.log(
              `[BookingStore] Adding fetched visit ${visitId} to cache.`,
            )
            this.visits.push(visitDoc)
          }
        })
      }
      return visitDoc
    } catch (error) {
      console.error(`[BookingStore] Failed to fetch visit ${visitId}`, error)
      this.errorStore.showError('Не удалось загрузить данные записи.')
      return null
    }
  }

  get selectedStaffMember() {
    if (this.selectedStaffId) {
      return this.staffStore.staff.find((s) => s.id === this.selectedStaffId)
    }
    // Если сотрудник не выбран, возвращаем первого в списке как значение по умолчанию.
    // Это сохраняет предыдущее поведение и обеспечивает работоспособность календаря
    // даже без явного выбора.
    return this.staffStore.staff[0] || null
  }

  get viewableWorkTimeRange() {
    // Теперь используется выбранный сотрудник или первый в списке по умолчанию.
    const staffMember = this.selectedStaffMember

    if (!staffMember || !staffMember.workDays) {
      // Return a default wide range if no data is available
      return { minTime: '07:00:00', maxTime: '21:00:00' }
    }

    let earliestStart = null
    let latestEnd = null

    // 1. Check the general weekly schedule
    Object.values(staffMember.workDays).forEach((daySchedule) => {
      if (daySchedule.isDayOff || !daySchedule.start || !daySchedule.end) {
        return
      }

      const currentStart = parse(daySchedule.start, 'HH:mm', new Date())
      const currentEnd = parse(daySchedule.end, 'HH:mm', new Date())

      if (!earliestStart || currentStart < earliestStart) {
        earliestStart = currentStart
      }
      if (!latestEnd || currentEnd > latestEnd) {
        latestEnd = currentEnd
      }
    })

    // 2. Check exceptions, as they might have different hours
    ;(staffMember.exceptions || []).forEach((exception) => {
      if (exception.isDayOff || !exception.start || !exception.end) {
        return
      }
      const currentStart = parse(exception.start, 'HH:mm', new Date())
      const currentEnd = parse(exception.end, 'HH:mm', new Date())

      if (!earliestStart || currentStart < earliestStart) {
        earliestStart = currentStart
      }
      if (!latestEnd || currentEnd > latestEnd) {
        latestEnd = currentEnd
      }
    })

    if (!earliestStart || !latestEnd) {
      return { minTime: '07:00:00', maxTime: '21:00:00' }
    }

    // Add buffer of 1 hour
    const minTimeDate = subHours(earliestStart, 1)
    const maxTimeDate = addHours(latestEnd, 1)

    const minTime = format(minTimeDate, 'HH:mm:ss')
    const maxTime =
      maxTimeDate.getDate() > latestEnd.getDate()
        ? '24:00:00'
        : format(maxTimeDate, 'HH:mm:ss')

    return { minTime, maxTime }
  }

  get monthViewBackgroundEvents() {
    // Теперь используется выбранный сотрудник или первый в списке по умолчанию.
    const staffMember = this.selectedStaffMember

    if (!staffMember || !staffMember.workDays) {
      return []
    }

    const events = []
    if (this._fetchedRanges.length === 0) {
      return []
    }

    // To avoid re-calculating for overlapping ranges, we can create a set of processed days
    const processedDays = new Set()

    this._fetchedRanges.forEach((range) => {
      const days = eachDayOfInterval({ start: range.start, end: range.end })

      for (const day of days) {
        const dateString = format(day, 'yyyy-MM-dd')
        if (processedDays.has(dateString)) {
          continue // Already processed this day from another (likely overlapping) range
        }
        processedDays.add(dateString)

        // 1. Check for exception
        const exception = staffMember.exceptions?.find((ex) => {
          if (!ex.date?.toDate) return false
          return format(ex.date.toDate(), 'yyyy-MM-dd') === dateString
        })

        let isWorkingDay = false
        if (exception) {
          if (!exception.isDayOff) {
            isWorkingDay = true
          }
        } else {
          // 2. Use general schedule
          const dayOfWeek = getDay(day)
          const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][
            dayOfWeek
          ]
          const generalDaySchedule = staffMember.workDays[dayKey]

          if (generalDaySchedule && !generalDaySchedule.isDayOff) {
            isWorkingDay = true
          }
        }

        if (isWorkingDay) {
          events.push({
            id: `work-bg-${dateString}`,
            start: dateString, // Дата без времени делает событие полнодневным
            allDay: true,
            display: 'background',
            groupId: 'working-hours',
            classNames: ['working-day-bg'], // Новый класс для стилизации
          })
        }
      }
    })

    return events
  }

  get timeGridBackgroundEvents() {
    // Теперь используется выбранный сотрудник или первый в списке по умолчанию.
    const staffMember = this.selectedStaffMember

    if (!staffMember || !staffMember.workDays) {
      return []
    }

    const events = []
    if (this._fetchedRanges.length === 0) {
      return []
    }

    // To avoid re-calculating for overlapping ranges, we can create a set of processed days
    const processedDays = new Set()

    this._fetchedRanges.forEach((range) => {
      const days = eachDayOfInterval({ start: range.start, end: range.end })

      for (const day of days) {
        const dateString = format(day, 'yyyy-MM-dd')
        if (processedDays.has(dateString)) {
          continue // Already processed this day from another (likely overlapping) range
        }
        processedDays.add(dateString)

        // 1. Check for exception
        const exception = staffMember.exceptions?.find((ex) => {
          if (!ex.date?.toDate) return false
          return format(ex.date.toDate(), 'yyyy-MM-dd') === dateString
        })

        let daySchedule
        if (exception) {
          if (exception.isDayOff) {
            continue // It's a day off, no background event
          }
          daySchedule = {
            start: exception.start,
            end: exception.end,
            breaks: exception.breaks || [],
          }
        } else {
          // 2. Use general schedule
          const dayOfWeek = getDay(day)
          const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][
            dayOfWeek
          ]
          const generalDaySchedule = staffMember.workDays[dayKey]

          if (!generalDaySchedule || generalDaySchedule.isDayOff) {
            continue // It's a day off
          }
          daySchedule = generalDaySchedule
        }

        // 3. Create background events from schedule, considering breaks
        const workStart = parse(daySchedule.start, 'HH:mm', day)
        const workEnd = parse(daySchedule.end, 'HH:mm', day)
        const breaks = (daySchedule.breaks || [])
          .map((b) => ({
            start: parse(b.start, 'HH:mm', day),
            end: parse(b.end, 'HH:mm', day),
          }))
          .sort((a, b) => a.start - b.start)

        let lastEventEnd = workStart

        for (const breakInterval of breaks) {
          // Add working segment before the break
          if (breakInterval.start > lastEventEnd) {
            events.push({
              id: `work-${dateString}-${format(lastEventEnd, 'HHmm')}`,
              start: lastEventEnd,
              end: breakInterval.start,
              display: 'background',
              groupId: 'working-hours',
              classNames: ['working-hours-bg'],
            })
          }
          lastEventEnd = new Date(
            Math.max(lastEventEnd.getTime(), breakInterval.end.getTime()),
          )
        }

        // Add the final working segment after the last break (or the whole day if no breaks)
        if (workEnd > lastEventEnd) {
          events.push({
            id: `work-${dateString}-${format(lastEventEnd, 'HHmm')}`,
            start: lastEventEnd,
            end: workEnd,
            display: 'background',
            groupId: 'working-hours',
            classNames: ['working-hours-bg'],
          })
        }
      }
    })

    return events
  }

  get calendarEvents() {
    return this.visits.map((visit) => {
      const client = this.clientsStore.clients.find(
        (c) => c.id === visit.clientId,
      )
      const startTime = visit.startTime?.toDate
        ? visit.startTime.toDate()
        : visit.startTime
      const endTime = visit.endTime?.toDate
        ? visit.endTime.toDate()
        : visit.endTime
      const currency = this.organizationStore.organization.currency || ''

      const classNames = []
      if (visit.status === 'canceled') {
        classNames.push('event-canceled')
      }

      return {
        id: visit.id,
        title: client?.name || 'Клиент',
        start: startTime,
        end: endTime,
        classNames,
        extendedProps: {
          ...visit,
          clientName: client?.name || 'Клиент',
          clientPhone: client?.phone || '',
          currency: currency,
        },
      }
    })
  }
}

export default BookingStore
